import { type IconData, IconRenderer, IconType } from '@affine/component';
import {
  emojiToFilename,
  getEmojiArtworkUrl,
  isEmojiArtworkMissing,
  markEmojiArtworkMissing,
} from '@affine/component/emoji-asset';
import * as litIcons from '@blocksuite/icons/lit';
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const getDocIconComponent = (icon: IconData) => {
  const Icon = () => <IconRenderer data={icon} />;
  Icon.displayName = 'DocIcon';
  return Icon;
};

/**
 * Renders emoji from the bundled PNG artwork (see
 * `@affine/component/ui/emoji`) with a native-text fallback, so doc icons
 * look identical on every platform including Lit-rendered surfaces.
 */
@customElement('affine-emoji-image')
export class AffineEmojiImage extends LitElement {
  @property({ type: String })
  accessor unicode = '';

  // Keyed by filename: a changed `unicode` re-evaluates the fallback instead
  // of reusing the failure of the previous emoji.
  @state()
  private accessor failedFor = '';

  @state()
  private accessor failed = false;

  protected override createRenderRoot() {
    return this;
  }

  protected override render() {
    const filename = emojiToFilename(this.unicode);
    const isFailed = this.failed && this.failedFor === filename;

    if (!this.unicode || isFailed) {
      return html`<span>${this.unicode}</span>`;
    }

    return html`<img
      src=${getEmojiArtworkUrl(this.unicode)}
      alt=${this.unicode}
      draggable="false"
      loading="lazy"
      decoding="async"
      style="width: 1em; height: 1em"
      @error=${() => {
        markEmojiArtworkMissing(filename);
        this.failedFor = filename;
        this.failed = true;
      }}
    />`;
  }
}

export const getDocIconComponentLit = (icon: IconData) => {
  return () => {
    if (icon.type === IconType.Emoji) {
      return html`<div class="icon">
        <affine-emoji-image .unicode=${icon.unicode}></affine-emoji-image>
      </div>`;
    }
    if (icon.type === IconType.AffineIcon) {
      return html`<div
        style="color: ${icon.color}; display: flex; align-items: center; justify-content: center;"
      >
        ${litIcons[`${icon.name}Icon` as keyof typeof litIcons]()}
      </div>`;
    }
    return null;
  };
};
